import { Suspense } from "react";
import { lazyWithChunkRecovery as lazy } from "@/lib/chunk-load-recovery";
import { Switch, Route, Redirect, Router as WouterRouter } from "wouter";
import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import {
  handleSessionExpired,
  installUnhandledRejectionGuard,
  isAuthError,
  notifyTransientError,
  shouldRetryQuery,
} from "@/lib/query-error-handling";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { LightboxProvider } from "@/components/image-lightbox";
import { ErrorBoundary } from "@/components/error-boundary";
import { AuthProvider, useAuth } from "@/lib/auth";
import { ProtectedRoute } from "@/components/layout";
import { DelayedPageSkeleton } from "@/components/page-skeleton";
import { Loader2 } from "lucide-react";
import { routeImporters } from "@/lib/route-prefetch";
import { CookieConsentProvider } from "@/lib/cookie-consent";
import { CookieConsentBanner } from "@/components/CookieConsentBanner";
import { useBodyPointerEventsGuard } from "@/hooks/use-body-pointer-events-guard";

// Every route is lazy-loaded so the initial JS bundle only contains the
// app shell + router. Importers are sourced from `routeImporters` so the
// hover-prefetch helpers in `lib/route-prefetch` reference the exact
// same dynamic-import call (and therefore the same Vite chunk) that
// React.lazy() will resolve when the route mounts. The Suspense
// boundary below uses a *delayed* skeleton so prefetched navigations
// flash nothing at all and only genuinely slow fetches reveal a
// fallback.
const Login = lazy(routeImporters.Login);
const Register = lazy(routeImporters.Register);
const WaitlistPage = lazy(routeImporters.Waitlist);
const MagicLinkVerify = lazy(routeImporters.MagicLinkVerify);
const VerifyEmail = lazy(routeImporters.VerifyEmail);
// Checkout module exports both default (Checkout) and named
// VerifyEmailPending — pull each through its own lazy() so the router
// can mount them at separate paths without an extra file.
const Checkout = lazy(routeImporters.Checkout);
const VerifyEmailPending = lazy(() => routeImporters.Checkout().then((m) => ({ default: m.VerifyEmailPending })));
const PaymentFailed = lazy(routeImporters.PaymentFailed);
const Welcome = lazy(routeImporters.Welcome);
const ForgotPassword = lazy(routeImporters.ForgotPassword);
const ResetPassword = lazy(routeImporters.ResetPassword);
const LandingPage = lazy(routeImporters.Landing);
const DashboardPage = lazy(routeImporters.Dashboard);
const ProductsPage = lazy(routeImporters.Products);
const NotFound = lazy(routeImporters.NotFound);
const PhotoshootHistoryPage = lazy(routeImporters.PhotoshootHistory);
const DirectorPage = lazy(routeImporters.Director);
const FinalImagesPage = lazy(routeImporters.FinalImages);
const AdminPage = lazy(routeImporters.Admin);
const AssetLibraryPage = lazy(routeImporters.AssetLibrary);
const PhotoDirectorLibraryPage = lazy(routeImporters.PhotoDirectorLibrary);
const UpsamplerPage = lazy(routeImporters.Upsampler);
const EditImagePage = lazy(routeImporters.EditImage);
const BillingPage = lazy(routeImporters.Billing);
const ModelManagementPage = lazy(routeImporters.ModelManagement);
const InfluencerManagementPage = lazy(routeImporters.InfluencerManagement);
const InfluencerRosterPage = lazy(routeImporters.InfluencerRoster);
const InfluencerProfilePage = lazy(routeImporters.InfluencerProfile);
const ProfilePage = lazy(routeImporters.Profile);
const ReportedBugsPage = lazy(routeImporters.ReportedBugs);
const ComingSoonPage = lazy(routeImporters.ComingSoon);
const AboutPage = lazy(routeImporters.About);
const ContactPage = lazy(routeImporters.Contact);
const PrivacyPage = lazy(routeImporters.Privacy);
const TermsPage = lazy(routeImporters.Terms);

installUnhandledRejectionGuard();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: shouldRetryQuery,
      retryDelay: (attemptIndex) =>
        Math.min(1000 * 2 ** attemptIndex, 8_000),
      staleTime: 30_000,
    },
    mutations: {
      retry: false,
    },
  },
  queryCache: new QueryCache({
    onError: (error) => {
      if (handleSessionExpired(queryClient, error)) return;
      if (isAuthError(error)) return;
      notifyTransientError(error);
    },
  }),
  mutationCache: new MutationCache({
    onError: (error) => {
      if (handleSessionExpired(queryClient, error)) return;
      if (isAuthError(error)) return;
      notifyTransientError(error);
    },
  }),
});

function _FullScreenSpinner() {
  return (
    <div className="flex h-screen items-center justify-center bg-background">
      <Loader2 className="w-10 h-10 animate-spin text-primary" />
    </div>
  );
}

function HomeRoute() {
  const { isAuthenticated, isLoading } = useAuth();
  if (isLoading) return <DelayedPageSkeleton />;
  if (isAuthenticated) return <ProtectedRoute component={DashboardPage} />;
  return <LandingPage />;
}

function ProductsRoute() {
  return <ProtectedRoute component={ProductsPage} />;
}

function Router() {
  return (
    <Suspense fallback={<DelayedPageSkeleton />}>
      <Switch>
        <Route path="/login" component={Login} />
        <Route path="/landing">{() => <Redirect to="/" />}</Route>
        <Route path="/register" component={Register} />
        <Route path="/waitlist" component={WaitlistPage} />
        <Route path="/magic-link/verify" component={MagicLinkVerify} />
        <Route path="/verify-email" component={VerifyEmail} />
        <Route path="/verify-email-pending" component={VerifyEmailPending} />
        <Route path="/forgot-password" component={ForgotPassword} />
        <Route path="/reset-password" component={ResetPassword} />
        <Route path="/checkout" component={Checkout} />
        <Route path="/payment-failed" component={PaymentFailed} />
        <Route path="/welcome" component={Welcome} />
        <Route path="/coming-soon/:slug" component={ComingSoonPage} />
        <Route path="/about" component={AboutPage} />
        <Route path="/contact" component={ContactPage} />
        <Route path="/privacy" component={PrivacyPage} />
        <Route path="/terms" component={TermsPage} />
        <Route path="/">
          {() => <HomeRoute />}
        </Route>
        <Route path="/dashboard">
          {() => <ProtectedRoute component={DashboardPage} />}
        </Route>
        <Route path="/products" component={ProductsRoute} />
        <Route path="/products/:productId/photoshoots">
          {(params) => <ProtectedRoute component={PhotoshootHistoryPage} params={params} />}
        </Route>
        <Route path="/photoshoots/:id/final-images">
          {(params) => <ProtectedRoute component={FinalImagesPage} params={params} />}
        </Route>
        <Route path="/photoshoots/:id">
          {(params) => <ProtectedRoute component={DirectorPage} params={params} />}
        </Route>
        <Route path="/assets">
          {() => <ProtectedRoute component={AssetLibraryPage} />}
        </Route>
        <Route path="/photo-director">
          {() => <ProtectedRoute component={PhotoDirectorLibraryPage} />}
        </Route>
        <Route path="/upsampler">
          {() => <ProtectedRoute component={UpsamplerPage} />}
        </Route>
        <Route path="/edit-image">
          {() => <ProtectedRoute component={EditImagePage} />}
        </Route>
        <Route path="/model-management">
          {() => <ProtectedRoute component={ModelManagementPage} />}
        </Route>
        <Route path="/influencer-management">
          {() => <ProtectedRoute component={InfluencerManagementPage} />}
        </Route>
        <Route path="/influencer-management/organisations/:orgId">
          {(params) => <ProtectedRoute component={InfluencerRosterPage} params={params} />}
        </Route>
        <Route path="/influencer-management/influencers/:influencerId">
          {(params) => <ProtectedRoute component={InfluencerProfilePage} params={params} />}
        </Route>
        <Route path="/billing">
          {() => <ProtectedRoute component={BillingPage} />}
        </Route>
        <Route path="/profile">
          {() => <ProtectedRoute component={ProfilePage} />}
        </Route>
        <Route path="/admin">
          {() => <ProtectedRoute component={AdminPage} adminOnly />}
        </Route>
        <Route path="/reported-bugs">
          {() => <ProtectedRoute component={ReportedBugsPage} adminOnly />}
        </Route>
        <Route component={NotFound} />
      </Switch>
    </Suspense>
  );
}

function App() {
  // Global self-heal for the Radix `pointer-events: none` body leak (see
  // hook docs). Mounted once here so it covers every overlay app-wide.
  useBodyPointerEventsGuard();

  // We intentionally do *not* clear the chunk-reload marker here. The
  // marker auto-expires after a short guard window (see
  // `recentlyReloaded` in chunk-load-recovery), which is the only safe
  // signal that the page has stayed up long enough to be considered
  // recovered. Clearing eagerly on mount would re-enable reloads
  // before the first post-reload lazy import has even resolved and
  // could cause a loop on persistently-failing chunks.

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <CookieConsentProvider>
            <TooltipProvider>
              <LightboxProvider>
                <WouterRouter base={import.meta.env.BASE_URL.replace(/\/$/, "")}>
                  <Router />
                </WouterRouter>
                <Toaster />
                <CookieConsentBanner />
              </LightboxProvider>
            </TooltipProvider>
          </CookieConsentProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
